import type { ChartConfig } from "~/components/evilcharts/ui/recharts-chart";
import type { RangeValue } from "~/context/AIVisibilityFiltersContext";
import type {
  AIVisibilityRun,
  ChartDataEntry,
  ChartScoresKey,
} from "~/types/AIVisibilityTypes";
import type { Sentiment } from "~/types/PromptTypes";

const RUN_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const RUN_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export const BRAND_CHART_COLORS = [
  "#ef4444",
  "#000000",
  "#22c55e",
  "#3b82f6",
  "#800000",
  "#14b8a6",
  "#f97316",
  "#8b5cf6",
  "#ef4444",
  "#eab308",
  "#0ea5e9",
  "#a855f7",
  "#6b7280",
];

export const BOT_BAR_COLORS = [
  "#f97316",
  "#3b82f6",
  "#8b5cf6",
  "#10b981",
  "#ef4444",
];

export const AI_TRAFFIC_CHART_COLORS = [
  "#f97316",
  "#3b82f6",
  "#8b5cf6",
  "#10b981",
  "#ef4444",
  "#eab308",
  "#06b6d4",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
];

export const PIE_CHART_COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#A28CFF",
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
];

export function formatRunAnalysisDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? value : RUN_DATE_FORMATTER.format(date);
}

export function formatRunDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : RUN_DATE_TIME_FORMATTER.format(date);
}

export function formatRunDuration(value: number | null): string {
  if (value === null) return "—";
  if (value < 60) return `${Math.round(value)}s`;

  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = Math.round(value % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function formatRunStatus(value: string | null): string {
  if (!value) return "Not started";
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function isInternalSupportEmail(
  email: string | null | undefined
): boolean {
  if (!email) return false;
  const domain = email.split("@").pop()?.toLowerCase();
  return domain === "zerply.ai" || domain === "wittypen.com";
}

export function isAIVisibilityRunActive(run: AIVisibilityRun): boolean {
  return (
    run.status.toLowerCase() === "running" ||
    run.recovery_status?.toLowerCase() === "running"
  );
}

function getRunRatioColorClass(ratio: number): string {
  if (ratio >= 1) return "text-green-600 dark:text-green-400";
  if (ratio >= 0.75) return "text-yellow-600 dark:text-yellow-400";
  if (ratio >= 0.5) return "text-orange-600 dark:text-orange-400";
  return "text-red-600 dark:text-red-400";
}

const RUN_METRIC_ACTIVE_COLOR_CLASS = "text-foreground";

export function getRunProgressColorClass(
  current: number,
  total: number,
  isActive = false
): string {
  if (isActive) return RUN_METRIC_ACTIVE_COLOR_CLASS;
  if (total <= 0) return "text-muted-foreground";
  return getRunRatioColorClass(current / total);
}

export function getRunFailureColorClass(
  failed: number,
  total: number,
  isActive = false
): string {
  if (isActive) return RUN_METRIC_ACTIVE_COLOR_CLASS;
  if (failed <= 0) return "text-green-600 dark:text-green-400";
  if (total <= 0) return "text-red-600 dark:text-red-400";
  return getRunRatioColorClass(1 - failed / total);
}

export function getRunSuccessRateColorClass(
  rate: number,
  isActive = false
): string {
  if (isActive) return RUN_METRIC_ACTIVE_COLOR_CLASS;
  return getRunRatioColorClass(rate / 100);
}

export function getRunStatusBadgeClass(status: string | null): string {
  const normalizedStatus = status?.toLowerCase() ?? "";

  if (normalizedStatus === "running") {
    return "border-transparent bg-muted text-muted-foreground";
  }

  if (normalizedStatus.includes("fail") || normalizedStatus.includes("error")) {
    return "border-transparent bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300";
  }

  if (normalizedStatus === "partial") {
    return "border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300";
  }

  if (
    normalizedStatus.includes("complete") ||
    normalizedStatus.includes("success") ||
    normalizedStatus === "scored" ||
    normalizedStatus === "collected"
  ) {
    return "border-transparent bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300";
  }

  return "border-transparent bg-muted/60 text-muted-foreground";
}

export function isRunStatusRunning(status: string | null): boolean {
  return status?.toLowerCase() === "running";
}

export const OPERATOR_LOGO_DOMAINS: Record<string, string> = {
  OpenAI: "openai.com",
  Anthropic: "anthropic.com",
  Perplexity: "perplexity.ai",
  Google: "google.com",
  Meta: "meta.com",
  ByteDance: "tiktok.com",
  Apple: "apple.com",
  Amazon: "amazon.com",
  "Common Crawl": "commoncrawl.org",
  DuckDuckGo: "duckduckgo.com",
  Mistral: "mistral.ai",
  xAI: "x.ai",
  "Moonshot AI": "kimi.com",
  Manus: "manus.im",
  "Devin AI": "devin.ai",
};

export function getOperatorLogoUrl(operator: string): string {
  const domain = OPERATOR_LOGO_DOMAINS[operator];
  if (domain) {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  }
  return "https://www.gravatar.com/avatar/?d=mp&s=32";
}

export const CHART_METRIC_DESCRIPTIONS: Record<string, string> = {
  citation_rate: "Share of mentions that cite your brand or content",
  mention_rate: "Share of tracked prompts where your brand is mentioned",
  bot_traffic: "AI crawlers visiting your site in this period",
  sentiment: "How AI answers that mention you describe your brand",
};

export function funnelStagePlain(stage: string | null | undefined): string {
  if (stage === "TOFU") return "Awareness";
  if (stage === "MOFU") return "Consideration";
  if (stage === "BOFU") return "Decision";
  return "—";
}

export function promptCategoryLabel(category: string | null | undefined) {
  if (category === "panel_category") return "Category";
  if (category === "panel_competitor") return "Competitor";
  if (category === "perception") return "Perception";
  return "—";
}

export function isBrandNamedStage(stage: string | null | undefined): boolean {
  return stage === "MOFU" || stage === "BOFU";
}

export const SENTIMENT_STYLES: Record<
  Sentiment,
  { label: string; pill: string; dot: string; text: string }
> = {
  positive: {
    label: "Positive",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-400",
  },
  neutral: {
    label: "Neutral",
    pill: "bg-stone-100 text-stone-600 dark:bg-stone-900 dark:text-stone-400",
    dot: "bg-stone-400",
    text: "text-stone-600 dark:text-stone-400",
  },
  negative: {
    label: "Negative",
    pill: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-400",
  },
};

export function accuracyRate(
  accurateCount: number,
  inaccurateCount: number
): number | null {
  const checkedCount = accurateCount + inaccurateCount;
  return checkedCount > 0 ? (accurateCount / checkedCount) * 100 : null;
}

const GRADE_ORDER = ["A", "B", "C", "D", "F"] as const;

function absoluteMentionGrade(rate: number): (typeof GRADE_ORDER)[number] {
  if (rate >= 80) return "A";
  if (rate >= 65) return "B";
  if (rate >= 50) return "C";
  if (rate >= 35) return "D";
  return "F";
}

function worseGrade(
  a: (typeof GRADE_ORDER)[number],
  b: (typeof GRADE_ORDER)[number]
): (typeof GRADE_ORDER)[number] {
  return GRADE_ORDER.indexOf(a) >= GRADE_ORDER.indexOf(b) ? a : b;
}

export function gradeFromCompetitiveVisibility(input: {
  mentionRate: number | null;
  rank: number | null;
  totalBrands: number;
  brandScore?: number | null;
  secondPlaceScore?: number | null;
  leaderScore?: number | null;
}): string | null {
  const rate = input.mentionRate;
  if (rate === null || Number.isNaN(rate)) return null;

  const rank = input.rank;
  const total = input.totalBrands;
  if (rank == null || total < 2) {
    return absoluteMentionGrade(rate);
  }

  const brandScore = input.brandScore ?? rate;
  const second = input.secondPlaceScore ?? null;
  const leader = input.leaderScore ?? null;

  let base: (typeof GRADE_ORDER)[number];
  if (rank === 1) {
    const clearLead =
      second == null || brandScore - second >= 3 || brandScore >= second * 1.25;
    base = clearLead ? "A" : "B";
  } else if (rank === 2) {
    const close = leader != null && leader - brandScore <= 5;
    base = close ? "B" : "C";
  } else if (rank <= Math.ceil(total / 2)) {
    base = "C";
  } else if (rank < total) {
    base = "D";
  } else {
    base = "F";
  }

  if (rate < 5) {
    return worseGrade(base, "D");
  }
  if (rate < 10) {
    return worseGrade(base, "C");
  }
  return base;
}

export function gradeFromStandings(input: {
  mentionRate: number | null;
  standings: Array<{
    rank: number;
    mentions: number;
    is_you?: boolean;
  }>;
}): string | null {
  const standings = input.standings;
  const you = standings.find((row) => row.is_you) ?? standings[0] ?? null;
  return gradeFromCompetitiveVisibility({
    mentionRate: input.mentionRate,
    rank: you?.rank ?? null,
    totalBrands: standings.length,
    brandScore: you?.mentions ?? null,
    secondPlaceScore: standings.length > 1 ? standings[1].mentions : null,
    leaderScore: standings.length > 0 ? standings[0].mentions : null,
  });
}

export type ContextRole =
  | "main_narrative"
  | "sources_references"
  | "disclaimer";

export const CONTEXT_ROLE_FIELD_MAPPING: Record<string, ContextRole> = {
  main_narrative_count: "main_narrative",
  sources_references_count: "sources_references",
  disclaimer_count: "disclaimer",
};

export const sentimentChartConfig = {
  count: {
    label: "Answers",
    colors: { light: ["#9ca3af"], dark: ["#6b7280"] },
  },
  positive: {
    label: "Positive",
    colors: { light: ["#10b981"], dark: ["#34d399"] },
  },
  neutral: {
    label: "Neutral",
    colors: { light: ["#a8a29e"], dark: ["#78716c"] },
  },
  negative: {
    label: "Negative",
    colors: { light: ["#ef4444"], dark: ["#f87171"] },
  },
} satisfies ChartConfig;

export const contextRoleChartConfig = {
  count: {
    label: "Count",
    colors: { light: ["#9ca3af"], dark: ["#6b7280"] },
  },
  main_narrative: {
    label: "Main Narrative",
    colors: { light: ["#f97316"], dark: ["#fb923c"] },
  },
  sources_references: {
    label: "Sources/References",
    colors: { light: ["#3b82f6"], dark: ["#60a5fa"] },
  },
  disclaimer: {
    label: "Disclaimer",
    colors: { light: ["#8b5cf6"], dark: ["#a78bfa"] },
  },
} satisfies ChartConfig;

export const COMPARISON_BRAND_SUFFIX = "compare";

function formatComparisonDayLabel(
  currentDate: string,
  comparisonDate: string
): string {
  const format = (date: string) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  return `${format(currentDate)} vs ${format(comparisonDate)}`;
}

export function filterDataByRange(
  data: ChartDataEntry[],
  fromDate: Date,
  toDate: Date
): ChartDataEntry[] {
  const start = new Date(fromDate);
  const end = new Date(toDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return data
    .filter((entry) => {
      if (!entry.date) return false;
      const entryDate = new Date(entry.date);
      if (isNaN(entryDate.getTime())) return false;
      entryDate.setHours(0, 0, 0, 0);
      return (
        entryDate.getTime() >= start.getTime() &&
        entryDate.getTime() <= end.getTime()
      );
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

type MergedChartRow = {
  date: string;
  dayLabel?: string;
  [key: string]: string | number | undefined;
};

type MergeComparisonOptions = {
  enabled: boolean;
  chartData: Record<string, Record<string, ChartDataEntry[]>>;
  fromDate: Date;
  toDate: Date;
};

function resolveBrandSeries(
  chartData: Record<string, Record<string, ChartDataEntry[]>>,
  brand: string,
  selectedRange: RangeValue
): ChartDataEntry[] {
  const brandData =
    chartData[brand]?.[selectedRange] || chartData[brand]?.["custom"] || [];
  return Array.isArray(brandData) ? brandData : [];
}

export function mergeDataForSelectedBrands(
  chartData: Record<string, Record<string, ChartDataEntry[]>>,
  selectedRange: RangeValue,
  selectedBrands: string[],
  metric: ChartScoresKey,
  fromDate: Date,
  toDate: Date,
  comparison?: MergeComparisonOptions
): MergedChartRow[] {
  const merged: Record<string, MergedChartRow> = {};
  const activeBrands = Object.keys(chartData).filter(
    (brand) => selectedBrands.length === 0 || selectedBrands.includes(brand)
  );

  activeBrands.forEach((brand) => {
    filterDataByRange(
      resolveBrandSeries(chartData, brand, selectedRange),
      fromDate,
      toDate
    ).forEach((entry) => {
      if (!merged[entry.date]) {
        merged[entry.date] = { date: entry.date };
      }
      merged[entry.date][brand] = entry.scores[metric];
    });
  });

  const currentRows = Object.values(merged).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  if (!comparison?.enabled) {
    return currentRows;
  }

  const suffix = COMPARISON_BRAND_SUFFIX;
  const comparisonSeriesByBrand = Object.fromEntries(
    activeBrands.map((brand) => [
      brand,
      filterDataByRange(
        resolveBrandSeries(comparison.chartData, brand, selectedRange),
        comparison.fromDate,
        comparison.toDate
      ),
    ])
  );

  return currentRows.map((row, index) => {
    const nextRow: MergedChartRow = { ...row };

    activeBrands.forEach((brand) => {
      const comparisonEntry = comparisonSeriesByBrand[brand]?.[index];
      if (!comparisonEntry) return;

      nextRow[`${brand}_${suffix}`] = comparisonEntry.scores[metric];
      nextRow.dayLabel = formatComparisonDayLabel(
        row.date,
        comparisonEntry.date
      );
    });

    return nextRow;
  });
}

export function expandBrandsForComparison(
  brands: string[],
  compareEnabled: boolean
): string[] {
  if (!compareEnabled) {
    return brands;
  }

  return brands.flatMap((brand) => [
    brand,
    `${brand}_${COMPARISON_BRAND_SUFFIX}`,
  ]);
}

export function isComparisonBrandKey(key: string): boolean {
  return key.endsWith(`_${COMPARISON_BRAND_SUFFIX}`);
}

export function baseBrandFromKey(key: string): string {
  return isComparisonBrandKey(key)
    ? key.slice(0, -`_${COMPARISON_BRAND_SUFFIX}`.length)
    : key;
}

export function isSameBrandSeries(
  selectedKey: string | null | undefined,
  seriesKey: string | null | undefined
): boolean {
  if (!selectedKey || !seriesKey) return false;
  return baseBrandFromKey(selectedKey) === baseBrandFromKey(seriesKey);
}

export interface GenerateColorMapOptions {
  user?: {
    teams?: Array<{
      id: number;
      team_id?: number;
      domain?: string;
      brand_color?: string;
    }>;
    team_id?: number;
  };
  competitorsList?: Array<{ domain: string; brand_color?: string }>;
  fallbackColors?: string[];
}

export function generateColorMap(
  brands: string[],
  options: GenerateColorMapOptions = {}
): Record<string, string> {
  const {
    user,
    competitorsList = [],
    fallbackColors = BRAND_CHART_COLORS,
  } = options;
  const colorMap: Record<string, string> = {};
  let index = 0;

  const currentTeam = user?.teams?.find(
    (team) => String(team.id) === String(user?.team_id)
  );

  brands.forEach((brand) => {
    if (
      currentTeam &&
      currentTeam.domain === brand &&
      currentTeam.brand_color
    ) {
      colorMap[brand] = currentTeam.brand_color;
    } else {
      const competitor = competitorsList.find((c) => c.domain === brand);
      if (competitor?.brand_color) {
        colorMap[brand] = competitor.brand_color;
      } else {
        colorMap[brand] = fallbackColors[index % fallbackColors.length];
        index++;
      }
    }
  });

  return colorMap;
}

